import { useEffect } from 'react';

import { useAppStore } from '../store/appStore';
import { wakeUp } from '../services/api';
import { Spinner } from './Spinner';

/**
 * Probes `/health` on first mount and shows a banner while the free-tier
 * Render instance cold-starts. Auto-dismisses once the API responds.
 *
 * Design choices
 * --------------
 * - Stays visible through the FIRST attempt's response time; if the response
 *   comes back under 300ms, the banner never actually paints (we only show
 *   on attempt >= 2, i.e. first retry). This keeps "happy-path" loads clean.
 * - Lives in the shell, not per-page, so the probe runs once per session.
 */
export function ColdStartBanner() {
  const { wakeState, setWakeState } = useAppStore();

  useEffect(() => {
    if (wakeState.status !== 'idle') return;
    setWakeState({ status: 'probing', attempts: 0 });

    let cancelled = false;
    wakeUp((attempt) => {
      if (cancelled) return;
      setWakeState({ status: 'probing', attempts: attempt });
    })
      .then(() => {
        if (!cancelled) setWakeState({ status: 'awake', attempts: 0 });
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setWakeState({
            status: 'failed',
            attempts: 0,
            error: err instanceof Error ? err.message : 'wake-up failed',
          });
      });

    return () => {
      cancelled = true;
    };
    // Intentionally run only once per app mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Only show from attempt 2 onwards to avoid a flash on fast responses.
  const shouldShow =
    (wakeState.status === 'probing' && wakeState.attempts >= 2) ||
    wakeState.status === 'failed';

  if (!shouldShow) return null;

  if (wakeState.status === 'failed') {
    return (
      <div
        role="alert"
        className="bg-danger/10 text-danger border-b border-danger/30 px-6 py-2 text-sm"
      >
        Could not reach the Raydn API. Check that the backend is running.
        {wakeState.error ? <span className="ml-2 opacity-70">({wakeState.error})</span> : null}
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="bg-brand/10 text-brand-dark border-b border-brand/30 px-6 py-2 text-sm flex items-center gap-3"
    >
      <Spinner className="h-4 w-4" />
      <span>
        Waking up the Raydn backend… the free-tier instance takes up to a minute on first request.
      </span>
      <span className="ml-auto opacity-70">attempt {wakeState.attempts}</span>
    </div>
  );
}
